import type {
  BlindWindow,
  Check,
  ComponentState,
  JudgedRun,
  QuarantineEntry,
  RunHistoryPage,
} from '@/domain/types';

/** One row of the run history: a run, a period of blindness, or a payload intake refused. */
export type TimelineItem =
  | { kind: 'run'; at: string; run: JudgedRun }
  | { kind: 'gap'; at: string; window: BlindWindow }
  | { kind: 'refused'; at: string; entry: QuarantineEntry };

export interface Combined {
  runs: JudgedRun[];
  windows: BlindWindow[];
  refused: QuarantineEntry[];
}

/** Flatten the loaded pages. Windows and refusals can appear on more than one page; keep each once. */
export function combinePages(pages: RunHistoryPage[]): Combined {
  const runs: JudgedRun[] = [];
  const seen = new Set<string>();
  const windows = new Map<string, BlindWindow>();
  const refused = new Map<string, QuarantineEntry>();
  for (const page of pages) {
    for (const run of page.runs) {
      if (seen.has(run.run_key)) continue;
      seen.add(run.run_key);
      runs.push(run);
    }
    for (const window of page.blind_windows) windows.set(window.opened_at, window);
    for (const entry of page.quarantine) refused.set(entry.quarantine_id, entry);
  }
  return { runs, windows: [...windows.values()], refused: [...refused.values()] };
}

/**
 * Newest first. A blind window sorts by when it opened, which is after the last run before it
 * and before the first run after it, so the gap row lands between those two runs. A window
 * still open sorts above every run. Refused payloads sort by when intake received them.
 */
export function buildTimeline({ runs, windows, refused }: Combined): TimelineItem[] {
  const items: TimelineItem[] = runs.map((run) => ({ kind: 'run', at: run.completed_at, run }));
  for (const window of windows) items.push({ kind: 'gap', at: window.opened_at, window });
  for (const entry of refused) items.push({ kind: 'refused', at: entry.received_at, entry });
  return items.sort((a, b) => b.at.localeCompare(a.at));
}

/** Checks in the configured order; anything the configuration does not know goes last, as sent. */
export function orderChecks(checks: Check[], configured: string[] | undefined): Check[] {
  if (!configured) return checks;
  const rank = new Map(configured.map((name, i) => [name, i]));
  return [...checks].sort(
    (a, b) =>
      (rank.get(a.name) ?? Number.MAX_SAFE_INTEGER) - (rank.get(b.name) ?? Number.MAX_SAFE_INTEGER),
  );
}

export function runDurationMs(run: JudgedRun): number {
  return Math.max(0, Date.parse(run.completed_at) - Date.parse(run.started_at));
}

/** A DOM id for a run's row, so the outcome strip can scroll to it. */
export function runRowId(runKey: string): string {
  return `run-${runKey.replace(/[^A-Za-z0-9]/g, '-')}`;
}

export function refusedRowId(quarantineId: string): string {
  return `refused-${quarantineId}`;
}

/** "Failing for 7 runs in a row", "Passing for 12 runs in a row", or nothing yet. */
export function streakSentence(state: ComponentState | undefined): string {
  if (!state || state.observed_outcome === null) return 'No runs yet';
  const plural = (n: number) => (n === 1 ? '1 run' : `${n} runs`);
  if (state.observed_outcome === 'FAIL') {
    const cause = state.driving_check ? `, driving check ${state.driving_check}` : '';
    return `Failing for ${plural(state.fail_streak)} in a row${cause}`;
  }
  return `Passing for ${plural(state.pass_streak)} in a row`;
}

export function humaniseReason(reason: string): string {
  return reason.replaceAll('_', ' ');
}
